import type { MarlinStore } from "@observe-tech/marlin-store/core";

const stripLeading = (path: string) =>
  path.replace(/^#/, "").replace(/^\/+/, "");

async function navigateToStoredScreenRoute(
  store: MarlinStore,
  navigate: (path: string) => void,
): Promise<void> {
  const screen = await store.screens.getScreen();
  const route = stripLeading(screen?.screenType ?? "");
  if (!route || route === stripLeading(window.location.pathname)) return;
  navigate(`/${route}`);
}

/**
 * Move this screen to the page a loaded layout template stored for it.
 *
 * Lives in the shell because the shell is mounted on every page, including
 * the ones whose app never listens (Feeding Analytics, the shell's own pages).
 * Only a load moves a screen: a save, a delete, or a grid edit clearing the
 * selection records no load and leaves every screen where it is.
 */
export function followLayoutTemplateLoads(
  store: MarlinStore,
  navigate: (path: string) => void,
): () => void {
  if (!store.capabilities.windows) return () => {};
  let lastLoadId: string | null | undefined;
  const subscription = store
    .observe(() => store.templates.getLastTemplateLoad())
    .subscribe({
      next: (load) => {
        const loadId = load?.id ?? null;
        const isNewLoad =
          lastLoadId !== undefined && loadId !== null && loadId !== lastLoadId;
        lastLoadId = loadId;
        if (!isNewLoad) return;
        navigateToStoredScreenRoute(store, navigate).catch(() => {});
      },
      error: (error) =>
        console.warn("[shell] following layout template loads failed", error),
    });
  return () => subscription.unsubscribe();
}
